import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { Button, FormHelperText, Grid, TextField } from "@mui/material";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import RichTextEditor from "components/Shared/RichTextEditor";
import FileDropzone from "components/Shared/FileDropzone";
import BaseSelect from "components/Shared/Base/BaseSelect";

import { useLazyGetDDLSystemUserListQuery} from "store/services/userApi";

import { useAuth, useDOUploadFile } from "hooks";

const INITIAL_VALUE = {
  title: "",
  priority: "",
  stage: "Open",
  description: "",
  responsible: "",
  attachments: [],
};

const FeedbackForm = ({
  feedbackDetail = null,
  isBusy = false,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();

  const priorityEnums = ["Low", "Medium", "High", "Critical"];
  const feedbackStageEnums = [
    "Open",
    "In Progress",
    "Verification",
    "Closed",
    "Discarded",
  ];

  const { viewEntity,isSystemAdmin } = useAuth();

  const { handleUploadFiles, uploadingFiles } = useDOUploadFile();

  // react form hook
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: INITIAL_VALUE,
  });

  // RTK QUERY
  const [
    fetchUsers,
    { data: userList = [], isLoading: loadingUsers, isFetching: fetchingUsers },
  ] = useLazyGetDDLSystemUserListQuery();

  const loading = isBusy || loadingUsers || fetchingUsers || uploadingFiles;

  const _mappedUsers = React.useMemo(
    () => userList?.map((el) => ({ label: el?.name, id: el?._id })),
    [userList]
  );

  // methods
  const handleSubmitFeedback = async (data) => {
    delete data?.responses;
    delete data?.history;

    const _feedbackPayload = { ...data, responsible: data?.responsible?.id };

    const [existingAttachments, newAttachments] = data?.attachments?.reduce(
      ([pass, fail], el) =>
        el?.publicId || el?.url ? [[...pass, el], fail] : [pass, [...fail, el]],
      [[], []]
    );

    if (!!newAttachments?.length) {
      const { uploadedFiles } = await handleUploadFiles(
        newAttachments,

        {
          folderName: "FEEDBACK",
          entity: viewEntity?.entity?._id,
          account: viewEntity?.entity?.account?._id,
        }
      );

      _feedbackPayload.attachments = [...existingAttachments, ...uploadedFiles];
    }
    onSubmit(_feedbackPayload);
  };

  React.useEffect(() => {
    if (!feedbackDetail) return;
    reset({
      ...feedbackDetail,
      responsible:
        {
          label: feedbackDetail?.responsible?.name,
          id: feedbackDetail?.responsible?._id,
        } ?? "",
    });
    return () => {
      reset(INITIAL_VALUE);
    };
  }, [feedbackDetail, reset]);

  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers, isSystemAdmin]);

  return (
    <AppForm loading={loading}>
      <AppForm.Content>
        <Grid container spacing={{ xs: 2, md: 6 }}>
          <Grid item xs={12} md={6}>
            <Controller
              name="title"
              control={control}
              rules={{ required: "Please provide a Title." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Title"
                  type="text"
                  size="small"
                  error={!!errors?.title}
                  helperText={errors?.title?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="priority"
              control={control}
              rules={{ required: "Please select a Priority." }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  fullWidth
                  placeholder="Select Priority"
                  options={priorityEnums}
                  error={!!errors?.priority}
                  helperText={errors?.priority?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="stage"
              control={control}
              rules={{ required: "Please select a Stage." }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  fullWidth
                  placeholder="Select Stage"
                  options={feedbackStageEnums}
                  error={!!errors?.stage}
                  helperText={errors?.stage?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="responsible"
              control={control}
              rules={{ required: "Please select a Responsible User." }}
              render={({ field }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Responsible"
                  loading={loadingUsers || fetchingUsers}
                  loadingText="Loading Responsible Users..."
                  options={_mappedUsers}
                  value={field?.value ?? ""}
                  onChange={(_, data) => {
                    setValue("responsible", data);
                  }}
                  error={!!errors?.responsible}
                  helperText={errors?.responsible?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={12}>
            <FormHelperText>Attachments</FormHelperText>

            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <FileDropzone
                  multiple
                  maxFiles={5}
                  files={field?.value ?? []}
                  onFileChange={(files) => setValue("attachments", files)}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={12}>
            <FormHelperText>Description*</FormHelperText>

            <Controller
              name="description"
              control={control}
              rules={{ required: "Please provide a Description." }}
              render={({ field: { onChange } }) => (
                <RichTextEditor
                  initialValue={
                    feedbackDetail?.description
                      ? feedbackDetail.description
                      : "<p>Enter Note Content.</p>"
                  }
                  onChange={onChange}
                  height={200}
                  error={!!errors.description}
                  helperText={errors?.description?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button disabled={isBusy} onClick={() => navigate(-1)}>
          Cancel
        </Button>

        <Button
          variant="contained"
          disabled={isBusy}
          onClick={handleSubmit(handleSubmitFeedback)}
        >
          {feedbackDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default FeedbackForm;
